import { AxiosResponse } from "axios";

import {
  IAddPatientRequest,
  IAddStaffRequest,
  IBulkImportStatus,
  IBulkImportSummary,
  ICreateCustomFieldRequest,
  ICustomFieldDefinition,
  ICustomFieldTemplate,
  IHospitalDetail,
  IMedicationSourceImpact,
  IMedicationSourceOption,
  IRefreshWorkflowConfigRequest,
  ISetWardCustomFieldsRequest,
  IUpdateCustomFieldRequest,
  IUpdateHospitalRequest,
  IWardCustomField,
} from "../../@types/admin/AdminHospitalDetails";
import { IAdminPatientListItem } from "../../@types/admin/AdminPatient";
import { IStaffListItem } from "../../@types/admin/AdminStaff";
import { IWard } from "../../@types/admin/AdminWard";
import { IPaginatedResponse } from "../../@types/PaginatedResponse";
import {
  ISetWardStandardFieldsRequest,
  IWardStandardField,
} from "../../@types/StandardField";
import { buildUploadFormData } from "../../utils/fileUpload";
import { api } from "../api";
import { fileTransferService } from "./fileTransferService";

/**
 * Hospital Details page (EN-5172 + EN-5175), migrated from the Retool
 * HospitalDetails app.
 *
 * Its three list reads are hospital-scoped routes on the Hospitals controller
 * rather than filters on the Wards / Patients / Staff lists. Those filters are
 * all optional, so letting `hospitals:access` reach them would let it omit the
 * id and read every ward, patient and staff member in the region; with the id
 * in the route the grant cannot widen past one hospital.
 */
export const hospitalDetailsService = {
  async getHospital(
    hospitalId: number
  ): Promise<AxiosResponse<IHospitalDetail>> {
    return api.get<IHospitalDetail>(`/Retool/Hospitals/${hospitalId}`);
  },

  async updateHospital(
    hospitalId: number,
    request: IUpdateHospitalRequest
  ): Promise<AxiosResponse<IHospitalDetail>> {
    return api.put<IHospitalDetail>(`/Retool/Hospitals/${hospitalId}`, request);
  },

  /** The medication catalogues a hospital can use, with their loaded release (EN-5267). */
  async getMedicationSources(): Promise<
    AxiosResponse<IMedicationSourceOption[]>
  > {
    return api.get<IMedicationSourceOption[]>(
      `/Retool/Hospitals/MedicationSources`
    );
  },

  /** Patients a medication source change would affect (EN-5267). */
  async getMedicationSourceImpact(
    hospitalId: number
  ): Promise<AxiosResponse<IMedicationSourceImpact>> {
    return api.get<IMedicationSourceImpact>(
      `/Retool/Hospitals/${hospitalId}/MedicationSourceImpact`
    );
  },

  /** The hospital's wards. */
  async getHospitalWards(
    hospitalId: number
  ): Promise<AxiosResponse<IPaginatedResponse<IWard[]>>> {
    return api.get<IPaginatedResponse<IWard[]>>(
      `/Retool/Hospitals/${hospitalId}/Wards`
    );
  },

  /**
   * The hospital's patients, across every one of its wards.
   *
   * Retool read the ward ids out of its own wards TABLE and passed them as an
   * array, so this list showed nothing until that table had loaded.
   */
  async getHospitalPatients(
    hospitalId: number,
    page: number,
    pageSize: number,
    search?: string
  ): Promise<AxiosResponse<IPaginatedResponse<IAdminPatientListItem[]>>> {
    return api.get<IPaginatedResponse<IAdminPatientListItem[]>>(
      `/Retool/Hospitals/${hospitalId}/Patients`,
      { params: { page, pageSize, search: search || undefined } }
    );
  },

  /** The hospital's staff, across its wards and the group itself. */
  async getHospitalStaff(
    hospitalId: number
  ): Promise<AxiosResponse<IPaginatedResponse<IStaffListItem[]>>> {
    return api.get<IPaginatedResponse<IStaffListItem[]>>(
      `/Retool/Hospitals/${hospitalId}/Staff`
    );
  },

  /** Adds a patient to one of the hospital's wards. */
  async addPatient(
    request: IAddPatientRequest
  ): Promise<AxiosResponse<unknown>> {
    return api.post("/Retool/Patients", request);
  },

  /**
   * Adds a staff member to the hospital.
   *
   * ⚠️ `role` is required by the API and absent from Retool's own body, which
   * is why its add-staff form fails — see `IAddStaffRequest`.
   */
  async addStaff(request: IAddStaffRequest): Promise<AxiosResponse<unknown>> {
    return api.post("/Retool/Staff", request);
  },

  /**
   * Tells one patient's app to re-read its workflow configuration.
   *
   * A SILENT push — the patient sees nothing. Retool called the query
   * `sendWorkflowPushNotification` and toasted "The workflow was refreshed",
   * which is what it does.
   */
  async refreshWorkflowConfig(userId: number): Promise<AxiosResponse<unknown>> {
    const request: IRefreshWorkflowConfigRequest = {
      userId,
      data: { MessageType: "Silent", configChanged: "workflow" },
      silent: true,
    };
    return api.post("/Retool/PushNotifications", request);
  },

  // ---------------------------------------------------------- custom fields

  /**
   * The hospital's custom-field definitions — Retool's "onboarding fields"
   * (`moduleCustomFields`). `includeInactive` is on because the page has to
   * offer reactivation.
   */
  async getCustomFields(
    hospitalGroupId: number
  ): Promise<AxiosResponse<ICustomFieldDefinition[]>> {
    return api.get<ICustomFieldDefinition[]>("/Retool/CustomFields", {
      params: { hospitalGroupId, includeInactive: true },
    });
  },

  async createCustomField(
    request: ICreateCustomFieldRequest
  ): Promise<AxiosResponse<ICustomFieldDefinition[]>> {
    // The endpoint is batch-shaped (it takes and returns a list); the modal
    // only ever creates one, so wrap it.
    return api.post<ICustomFieldDefinition[]>("/Retool/CustomFields", [
      request,
    ]);
  },

  async updateCustomField(
    id: string,
    request: IUpdateCustomFieldRequest
  ): Promise<AxiosResponse<ICustomFieldDefinition>> {
    return api.put<ICustomFieldDefinition>(
      `/Retool/CustomFields/${id}`,
      request
    );
  },

  /**
   * Deactivates a definition. DELETE, but not a delete: the answers already
   * recorded against it stay, and `PUT { isActive: true }` brings it back.
   */
  async deactivateCustomField(id: string): Promise<AxiosResponse<unknown>> {
    return api.delete(`/Retool/CustomFields/${id}`);
  },

  /** Presets, flagged with whether this hospital already has each one. */
  async getCustomFieldTemplates(
    hospitalGroupId: number
  ): Promise<AxiosResponse<ICustomFieldTemplate[]>> {
    return api.get<ICustomFieldTemplate[]>("/Retool/CustomFields/Templates", {
      params: { hospitalGroupId },
    });
  },

  async createCustomFieldsFromTemplates(
    hospitalGroupId: number,
    templateKeys: string[]
  ): Promise<AxiosResponse<ICustomFieldDefinition[]>> {
    return api.post<ICustomFieldDefinition[]>(
      "/Retool/CustomFields/FromTemplates",
      { hospitalGroupId, templateKeys }
    );
  },

  /** How one ward uses the hospital's definitions. */
  async getWardCustomFields(
    wardId: number
  ): Promise<AxiosResponse<IWardCustomField[]>> {
    return api.get<IWardCustomField[]>(`/Retool/CustomFields/Wards/${wardId}`);
  },

  /** Replaces the ward's whole configuration — not a delta. */
  async setWardCustomFields(
    wardId: number,
    request: ISetWardCustomFieldsRequest
  ): Promise<AxiosResponse<unknown>> {
    return api.put(`/Retool/CustomFields/Wards/${wardId}`, request);
  },

  /**
   * EN-5192: the ward's settings for the BUILT-IN onboarding fields (sex, ethnicity, height,
   * date of birth, phone number, hospital ID). Returns the whole fixed catalogue with its
   * effective values, so an unconfigured ward shows its defaults rather than an empty table.
   */
  async getWardStandardFields(
    wardId: number
  ): Promise<AxiosResponse<IWardStandardField[]>> {
    return api.get<IWardStandardField[]>(
      `/Retool/Wards/${wardId}/StandardFields`
    );
  },

  /** Replaces the ward's whole standard-field configuration — not a delta. */
  async setWardStandardFields(
    wardId: number,
    request: ISetWardStandardFieldsRequest
  ): Promise<AxiosResponse<unknown>> {
    return api.put(`/Retool/Wards/${wardId}/StandardFields`, request);
  },

  // ------------------------------------------------------------ bulk import

  async getBulkImports(): Promise<
    AxiosResponse<IPaginatedResponse<IBulkImportSummary[]>>
  > {
    return api.get<IPaginatedResponse<IBulkImportSummary[]>>(
      "/Retool/Patients/BulkImports"
    );
  },

  /** One job, with its per-row results once it has run. */
  async getBulkImportStatus(
    jobId: string
  ): Promise<AxiosResponse<IBulkImportStatus>> {
    return api.get<IBulkImportStatus>(`/Retool/Patients/BulkImports/${jobId}`);
  },

  /**
   * Queues a CSV import into one ward. Multipart: the API binds `File` and
   * `WardId` from the form.
   */
  async startBulkImport(
    file: File,
    wardId: number
  ): Promise<AxiosResponse<IBulkImportStatus>> {
    return fileTransferService.upload<IBulkImportStatus>({
      url: "/Retool/Patients/BulkImports",
      method: "POST",
      formData: buildUploadFormData({ File: file, WardId: wardId }),
    });
  },
};
